import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Page,
  Layout,
  Card,
  Text,
  TextField,
  Button,
  Badge,
  Avatar,
  Select,
  Spinner,
  InlineStack,
  Divider
} from '@shopify/polaris'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

import { useAuth } from '@/contexts/AuthContext'
import { useSocket } from '@/contexts/SocketContext'
import { conversationApi, messageApi, storeApi } from '@/services/api'
import type { ConversationStatus } from '@/types'

export default function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isConnected } = useSocket()
  const queryClient = useQueryClient()
  
  const [messageText, setMessageText] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: conversationData, isLoading } = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => conversationApi.getConversation(id!),
    enabled: !!id
  })

  const { data: agentsData } = useQuery({
    queryKey: ['agents', user?.storeId],
    queryFn: () => storeApi.getAgents(user!.storeId),
    enabled: !!user?.storeId
  })

  const sendMessageMutation = useMutation({
    mutationFn: messageApi.sendMessage,
    onSuccess: () => {
      setMessageText('')
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      scrollToBottom()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to send message')
    }
  })

  const updateConversationMutation = useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: string; data: any }) =>
      conversationApi.updateConversation(conversationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    }
  })

  const assignConversationMutation = useMutation({
    mutationFn: ({ conversationId, agentId }: { conversationId: string; agentId: string }) =>
      conversationApi.assignConversation(conversationId, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      toast.success('Conversation assigned successfully')
    }
  })

  const conversation = conversationData?.data.data.conversation
  const messages = conversation?.messages || []
  const agents = agentsData?.data.data.agents || []

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    // Mark messages as read when conversation is opened
    if (id) {
      messageApi.markAsRead(id)
    }
  }, [id])

  useEffect(() => {
    // Listen for real-time message updates
    const handleNewMessage = (event: CustomEvent) => {
      if (event.detail.conversationId === id) {
        queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      }
    }

    const handleConversationUpdate = (event: CustomEvent) => {
      if (event.detail.conversationId === id) {
        queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      }
    }

    window.addEventListener('new-message', handleNewMessage as EventListener)
    window.addEventListener('conversation-updated', handleConversationUpdate as EventListener)

    return () => {
      window.removeEventListener('new-message', handleNewMessage as EventListener)
      window.removeEventListener('conversation-updated', handleConversationUpdate as EventListener)
    }
  }, [id, queryClient])

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!messageText.trim() || !id) return

    setSending(true)
    try {
      await sendMessageMutation.mutateAsync({
        conversationId: id,
        content: messageText.trim(),
        messageType: 'TEXT'
      })
    } finally {
      setSending(false)
    }
  }

  const handleStatusChange = (status: ConversationStatus) => {
    if (!id) return
    updateConversationMutation.mutate({
      conversationId: id,
      data: { status }
    })
  }

  const handleAssignAgent = (agentId: string) => {
    if (!id) return
    assignConversationMutation.mutate({
      conversationId: id,
      agentId
    })
  }

  const getStatusBadge = (status: ConversationStatus) => {
    const statusMap = {
      NEW: { status: 'info' as const, label: 'New' },
      ASSIGNED: { status: 'attention' as const, label: 'Assigned' },
      IN_PROGRESS: { status: 'warning' as const, label: 'In Progress' },
      RESOLVED: { status: 'success' as const, label: 'Resolved' },
      CLOSED: { status: 'subdued' as const, label: 'Closed' },
      ARCHIVED: { status: 'subdued' as const, label: 'Archived' }
    }
    
    const config = statusMap[status] || statusMap.NEW
    return <Badge status={config.status}>{config.label}</Badge>
  }

  const statusOptions = [
    { label: 'New', value: 'NEW' },
    { label: 'Assigned', value: 'ASSIGNED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Resolved', value: 'RESOLVED' },
    { label: 'Closed', value: 'CLOSED' }
  ]

  const agentOptions = [
    { label: 'Unassigned', value: '' },
    ...agents.map(agent => ({ label: agent.name, value: agent.id }))
  ]

  if (isLoading) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spinner accessibilityLabel="Loading conversation" size="large" />
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  if (!conversation) {
    return (
      <Page title="Conversation Not Found">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Text variant="headingMd" as="h3">
                  Conversation not found
                </Text>
                <div style={{ marginTop: '16px' }}>
                  <Button onClick={() => navigate('/conversations')}>
                    Back to Conversations
                  </Button>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page
      title={conversation.customer?.name || conversation.customer?.whatsappPhoneNumber}
      subtitle={`Conversation • ${getStatusBadge(conversation.status)}`}
      backAction={{ content: 'Conversations', onAction: () => navigate('/conversations') }}
      secondaryActions={[
        {
          content: 'View Customer',
          onAction: () => navigate(`/customers/${conversation.customer?.id}`)
        }
      ]}
    >
      <Layout>
        <Layout.Section secondary>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text variant="headingMd" as="h3">
                Customer Info
              </Text>
              
              <div style={{ marginTop: '16px' }}>
                <InlineStack blockAlign="center" gap="200">
                  <Avatar
                    customer
                    name={conversation.customer?.name || conversation.customer?.whatsappPhoneNumber}
                    size="medium"
                  />
                  <div>
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                      {conversation.customer?.name || 'Unknown'}
                    </Text>
                    <Text variant="bodySm" color="subdued" as="p">
                      {conversation.customer?.whatsappPhoneNumber}
                    </Text>
                  </div>
                </InlineStack>
              </div>

              {conversation.customer?.email && (
                <div style={{ marginTop: '12px' }}>
                  <Text variant="bodySm" color="subdued" as="p">
                    Email: {conversation.customer.email}
                  </Text>
                </div>
              )}

              <Divider />

              <div style={{ marginTop: '16px' }}>
                <Text variant="bodyMd" fontWeight="semibold" as="p">
                  Status
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Select
                    label=""
                    options={statusOptions}
                    value={conversation.status}
                    onChange={handleStatusChange}
                  />
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <Text variant="bodyMd" fontWeight="semibold" as="p">
                  Assigned Agent
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Select
                    label=""
                    options={agentOptions}
                    value={conversation.assignedAgentId || ''}
                    onChange={handleAssignAgent}
                  />
                </div>
              </div>

              {conversation.order && (
                <>
                  <Divider />
                  <div style={{ marginTop: '16px' }}>
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                      Linked Order
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      <Badge>#{conversation.order.orderNumber}</Badge>
                      <Text variant="bodySm" color="subdued" as="p">
                        ${conversation.order.totalAmount} • {conversation.order.status}
                      </Text>
                    </div>
                  </div>
                </>
              )}

              <div style={{ marginTop: '16px', fontSize: '12px', color: '#6d7175' }}>
                <Text variant="bodySm" color="subdued" as="p">
                  Connection: {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
                </Text>
              </div>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '16px',
                borderBottom: '1px solid #e1e3e5'
              }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text variant="bodyMd" color="subdued" as="p">
                      No messages yet. Start the conversation!
                    </Text>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        style={{
                          display: 'flex',
                          justifyContent: message.direction === 'OUTBOUND' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '70%',
                            padding: '12px 16px',
                            borderRadius: '18px',
                            backgroundColor: message.direction === 'OUTBOUND' ? '#007ace' : '#f6f6f7',
                            color: message.direction === 'OUTBOUND' ? 'white' : '#202223'
                          }}
                        >
                          <Text variant="bodyMd" as="p">
                            {message.content}
                          </Text>
                          <div style={{ 
                            marginTop: '4px', 
                            fontSize: '11px', 
                            opacity: 0.7 
                          }}>
                            {format(new Date(message.createdAt), 'HH:mm')}
                            {message.direction === 'OUTBOUND' && (
                              <span style={{ marginLeft: '4px' }}>
                                {message.status === 'DELIVERED' ? '✓✓' : 
                                 message.status === 'READ' ? '✓✓' : '✓'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              <form onSubmit={handleSendMessage} style={{ padding: '16px' }}>
                <InlineStack gap="400">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label=""
                      value={messageText}
                      onChange={setMessageText}
                      placeholder="Type your message..."
                      multiline={2}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    submit
                    primary
                    loading={sending}
                    disabled={!messageText.trim() || conversation.status === 'CLOSED'}
                  >
                    Send
                  </Button>
                </InlineStack>
              </form>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}